/**
 * useDashboard — fetches the aggregate `/api/dashboard` endpoint.
 *
 * Wave 29 — Phase 3b dashboards migration.
 *
 * The CAIO, CEO and Commercial Summary pages each read 8-12 slices of
 * the tenant bundle. Rather than issuing 10 parallel REST calls per
 * page load, this hook hits a single aggregate endpoint that returns
 * the curated subset each dashboard needs.
 *
 * Same Promise.resolve().then(...) pattern + `inFlight` ref guard as
 * useEntityList (see /app/frontend/src/lib/useEntity.js for rationale).
 */
import { useCallback, useEffect, useReducer, useRef } from "react";
import { api, ApiError } from "@/lib/api";

const INITIAL = { data: null, loading: true, error: null, lastSyncedAt: null };

function reducer(state, action) {
  switch (action.kind) {
    case "start":
      return { ...state, loading: true, error: null };
    case "success":
      return {
        data: action.data,
        loading: false,
        error: null,
        lastSyncedAt: action.lastSyncedAt,
      };
    case "failure":
      return { ...state, data: null, loading: false, error: action.error };
    default:
      return state;
  }
}

export function useDashboard() {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const inFlight = useRef(0);

  const fetchOnce = useCallback(() => {
    const seq = ++inFlight.current;
    Promise.resolve()
      .then(() => { if (seq === inFlight.current) dispatch({ kind: "start" }); })
      .then(() => api.get("/api/dashboard"))
      .then((data) => {
        if (seq !== inFlight.current) return;
        dispatch({ kind: "success", data, lastSyncedAt: new Date() });
      })
      .catch((e) => {
        if (seq !== inFlight.current) return;
        dispatch({
          kind: "failure",
          error: e instanceof ApiError ? e : new ApiError(0, String(e)),
        });
      });
  }, []);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    lastSyncedAt: state.lastSyncedAt,
    refetch: fetchOnce,
  };
}
